import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/LogHistory';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const LOG_HISTORY_API_REDUCER_KEY = 'log-histories';
export const LOG_HISTORY_PATH = 'log-histories';

export const logHistoryApi = createApi({
    reducerPath: LOG_HISTORY_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['LogHistory', 'LogHistories'],
    endpoints: (builder) => ({
        getLogHistoryList: builder.query<PaginationResult<Entity.LogHistory>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ LOG_HISTORY_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['LogHistories'],
        }),
        getLogHistory: builder.query<Entity.LogHistory, Entity.PrimaryLogHistory>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = logHistoryNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ LOG_HISTORY_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['LogHistory'],
        }),
        getLogHistoryMultiple: builder.query<Entity.LogHistory, Entity.PrimaryLogHistory[]>({
            query: (ids) => {
                return ({
                    url: `/${ LOG_HISTORY_PATH }/ids${Entity.PrimaryLogHistory.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['LogHistories'],
        }),
        getLogHistoryPresentation: builder.query<PaginationResult<Entity.LogHistory>, Entity.PresentationLogHistoryRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ LOG_HISTORY_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['LogHistories'],
        }),
        getLogHistoryByContext: builder.query<PaginationResult<Entity.LogHistory>, Entity.FilterByContextRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ LOG_HISTORY_PATH }/find/byContext?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['LogHistories'],
        }),
        
        insertLogHistory: builder.mutation<Entity.LogHistory, Entity.CreateLogHistory>({
            query: (toInsert) => {
                return ({
                    url: `/${ LOG_HISTORY_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    logHistoryApi.util.upsertQueryData('getLogHistory', Entity.PrimaryLogHistory.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['LogHistories'],
        }),
        insertMultipleLogHistory: builder.mutation<Entity.LogHistory[], Entity.CreateLogHistory[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ LOG_HISTORY_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            logHistoryApi.util.upsertQueryData('getLogHistory', Entity.PrimaryLogHistory.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['LogHistories'],
        }),
        
        deleteLogHistory: builder.mutation<Entity.LogHistory, Entity.LogHistory>({
            query: (toDelete) => {
                return ({
                    url: `/${ LOG_HISTORY_PATH }/${Entity.PrimaryLogHistory.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['LogHistories'],
        }),
        deleteMultipleLogHistory: builder.mutation<Entity.LogHistory[], Entity.PrimaryLogHistory[]>({
            query: (ids) => {
                return ({
                    url: `/${ LOG_HISTORY_PATH }/batch${Entity.PrimaryLogHistory.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['LogHistories'],
        }),
    }),
})

export const [logHistoryNormalizer, logHistoryNormalizerMiddleware] = createNormalizationMiddleware(LOG_HISTORY_API_REDUCER_KEY, logHistoryApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetLogHistoryListQuery,
    useGetLogHistoryMultipleQuery,
    useGetLogHistoryPresentationQuery,
    useGetLogHistoryQuery,
    useInsertLogHistoryMutation,
    useInsertMultipleLogHistoryMutation,
    
    useDeleteLogHistoryMutation,
    useDeleteMultipleLogHistoryMutation,
    useGetLogHistoryByContextQuery,
    
} = logHistoryApi
